import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
    Box, Typography, Paper, CircularProgress, Divider, 
    IconButton, Alert, Grid, Card, CardContent 
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import PhoneIcon from "@mui/icons-material/Phone";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'; 
import { useTheme } from "@mui/material";
import { tokens } from "../../../../../theme.js";
import axios from "axios";
const EmployeeDetails = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id } = useParams();
    const navigate = useNavigate(); 
    const [employee, setEmployee] = useState(null);
    const [bank, setBank] = useState(null);
    const [address, setAddress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [employeeDocs, setEmployeeDocs] = useState({});
    const [workExperience, setWorkExperience] = useState(null);
    const [emergencyContact, setEmergencyContact] = useState(null);
    const [qualificationDocs, setQualificationDocs] = useState({});
    const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;
    
    useEffect(() => {
        const fetchEmployeeData = async () => {
            
            const email = id;
    
            if (!email) {
                setError("Employee ID not found in URL.");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError(null);
    
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL_HRM}/get-employee-data/${email}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`
                        }
                    }
                );
                const data = response.data;
    
                if (data.error) {
                    setError(data.error);
                    setLoading(false);
                    return;
                }
    
                // Map API response fields to state
                setEmployee(data.personal_details || {});
                setAddress(data.address || {});
                setBank(data.bank_details || {});
                setEmployeeDocs(data.employee_documents || {});
                setQualificationDocs(data.qualification_documents || {});
                setWorkExperience(data.work_experience || {});
                setEmergencyContact(data.emergency_contact || {});
            } catch (err) {
                console.error("Error fetching employee details:", err);
                setError("Failed to load employee details. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchEmployeeData();
    }, [id]);
    

    if (loading) return (
        <CircularProgress 
            sx={{ 
                display: "block", 
                margin: "auto", 
                mt: 4, 
                color: colors.greenAccent[500] 
            }}  
        />
    );

    return (
        <Box
            sx={{
                flexGrow: 1,
                width: "100%",
                minHeight: "100vh", 
                overflowY: "auto",
                p: 3,
                display: "flex",
                justifyContent: "center",
   
            }}
        >
            <Paper
                elevation={4}
                sx={{
                    width: "90%",
                    maxWidth: "1200px",
                    padding: 4,
                    borderRadius: 3,
                    backgroundColor: colors.primary[400],
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Back Button */}
                <Box 
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        width: '100%'
                    }}
                >
                    <IconButton 
                        onClick={() => navigate("/material/EmpList")} 
                        sx={{ 
                            color: colors.blueAccent[500],
                            borderRadius: '50%',
                            transition: 'background-color 0.3s ease',
                            '&:hover': {
                                backgroundColor: colors.blueAccent[700],
                                color: colors.grey[100]
                            }
                        }}
                    >
                        <ArrowBackIcon fontSize="large" />
                    </IconButton>
                </Box>
                {error ? (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 2,
                            backgroundColor: colors.redAccent[700],
                            color: colors.grey[100]
                        }}
                    >
                        {error}
                    </Alert>
                ) : (
                    <>
                        {/* Title */}
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                textAlign: "center", 
                                fontWeight: "bold", 
                                mb: 3, 
                                color: colors.blueAccent[500] 
                            }}
                        >
                            {employee?.First_Name || "Employee"} Details
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Personal Details & Contact Details in One Row */}
                            <Grid item xs={12} md={6}>
                                <Card 
                                    sx={{ 
                                        borderLeft: `5px solid ${colors.blueAccent[500]}`, 
                                        backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900], 
                                        height: "100%"
                                    }}
                                >
                                    <CardContent>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                fontWeight: "bold", 
                                                mb: 2, 
                                                color: colors.blueAccent[500] 
                                            }}
                                        >
                                            <PersonIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Personal Details
                                        </Typography>
                                        <Typography color={colors.grey[100]} >
                                            <strong>Full Name:</strong> {`${employee?.First_Name || " "} ${employee?.Middle_Name || ""} ${employee?.Last_Name || " "}`}
                                        </Typography>
                                        <Typography color={colors.grey[100]}><strong>Legal Name:</strong> {employee?.Legal_Name || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Gender:</strong> {employee?.Gender || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Marital Status:</strong> {employee?.Marital_Status || " "}</Typography>
                                       <Typography>
                                        <strong>Date of Birth:</strong>{" "}
                                        {employee?.Date_Of_Birth
                                            ? new Date(employee.Date_Of_Birth).toLocaleDateString('en-US', {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: '2-digit',
                                            })
                                            : " "}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card  
                                    sx={{ 
                                        borderLeft: `5px solid ${colors.greenAccent[500]}`, 
                                        backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900],
                                        height: "100%"
                                    }}
                                >
                                    <CardContent>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                fontWeight: "bold", 
                                                mb: 2, 
                                                color: colors.greenAccent[500] 
                                            }}
                                        >
                                            <PhoneIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Contact Details
                                        </Typography>
                                        <Typography color={colors.grey[100]}><strong>Mobile Number:</strong> {employee?.Mobile_Number || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Personal Email:</strong> {employee?.Personal_Email_ID || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Corporate Email:</strong> {employee?.Corporate_Email_ID || " "}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {bank && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.redAccent[500]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900],
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.redAccent[500] 
                                                }}
                                            >
                                                <AccountBalanceIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Bank Details
                                            </Typography>
                                            <Typography color={colors.grey[100]}><strong>UPI ID:</strong> {bank?.UPI_ID|| " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Bank Account Number:</strong> {bank?.Bank_Account_Number || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>PAN Number:</strong> {bank?.PAN_Number || " "}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Job Details & Address Details in One Row */}
                            <Grid item xs={12} md={6}>
                                <Card 
                                    sx={{ 
                                        borderLeft: `5px solid ${colors.grey[500]}`, 
                                        backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900], 
                                        height: "100%"
                                    }}
                                >
                                    <CardContent>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                fontWeight: "bold", 
                                                mb: 2, 
                                                color: colors.greenAccent[500] 
                                            }}
                                        >
                                            <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Job Details
                                        </Typography>
                                        <Typography color={colors.grey[100]}><strong>Job Title:</strong> {employee?.Job_Title_ID || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Designation:</strong> {employee?.Designation || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Probationary Period:</strong> {employee?.Probationary_Period || " "}</Typography>
                                        <Typography color={colors.grey[100]}><strong>Branch:</strong> {employee?.Branch_ID || " "}</Typography>
                                        <Typography>
                                            <strong>Joining Date:</strong>{" "}
                                            {(() => {
                                                const joiningDate = employee?.Joining_Date;
                                                if (!joiningDate) return " ";

                                                const parsedDate = new Date(Date.parse(joiningDate));
                                                return isNaN(parsedDate)
                                                ? "Invalid Date"
                                                : parsedDate.toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: '2-digit',
                                                    });
                                            })()}
                                            </Typography>

                                    </CardContent>
                                </Card>
                            </Grid>

                            {address && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.blueAccent[300]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900], 
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.blueAccent[300] 
                                                }}
                                            >
                                                <HomeIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Address Details
                                            </Typography>
                                            <Typography color={colors.grey[100]}><strong>Present Address:</strong> {address?.Present_Address || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Present City:</strong> {address?.Present_City || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Country:</strong> {address?.Country || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Permanent Address:</strong> {address?.Permanent_Address || " "}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Employee Documents & Qualification Documents in One Row */}
                            {employeeDocs && Object.keys(employeeDocs).length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.greenAccent[300]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900], 
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.greenAccent[300] 
                                                }}
                                            >
                                                📄 Employee Documents
                                            </Typography>
                                            {Object.entries(employeeDocs).map(([key, url]) => (
                                                <Typography key={key} color={colors.grey[100]}>
                                                    <strong>{key.replace("_", " ").toUpperCase()}:</strong>  
                                                    <a 
                                                        href={url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer" 
                                                        style={{ 
                                                            color: colors.blueAccent[500], 
                                                            textDecoration: "none", 
                                                            marginLeft: "5px" 
                                                        }}
                                                    >
                                                        View Document
                                                    </a>
                                                </Typography>
                                            ))}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Qualification Documents Card */}
                            {qualificationDocs && Object.keys(qualificationDocs).length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.redAccent[300]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900],
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.redAccent[300] 
                                                }}
                                            >
                                                🎓 Qualification Documents
                                            </Typography>
                                            {Object.entries(qualificationDocs).map(([key, url]) => (
                                                <Typography key={key} sx={{ mb: 1 }}>
                                                    <strong>{key.replace(/_/g, " ").toUpperCase()}:</strong>
                                                    <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: "#1e88e5",
                                                        textDecoration: "none",
                                                        marginLeft: "5px",
                                                    }}
                                                    >
                                                    View Document
                                                    </a>
                                                </Typography>
                                            ))}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Work Experience */}
                            {workExperience && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.grey[300]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900],
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.redAccent[500] 
                                                }}
                                            >
                                                <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Work Experience
                                            </Typography>
                                            <Typography color={colors.grey[100]}><strong>Professional Reference 1:</strong> {workExperience.professional_reference1 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Reference 1 Contact:</strong> {workExperience.professional_reference1_contact_number || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Professional Reference 2:</strong> {workExperience.professional_reference2 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Reference 2 Contact:</strong> {workExperience.professional_reference2_contact_number || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Skill Sets:</strong> {workExperience.skill_sets || " "}</Typography>

                                            {workExperience?.uploaded_files?.experience_certificate ? (
                                                <Typography>
                                                    <strong>Experience Certificate:</strong>
                                                    <a
                                                        href={workExperience.uploaded_files.experience_certificate}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: "#1e88e5", textDecoration: "none", marginLeft: "5px" }}
                                                    >
                                                        View Certificate
                                                    </a>
                                                        </Typography>
                                                ) : (
                                                    <Typography>No experience certificate uploaded.</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Emergency Contact Details Card */}
                            {emergencyContact && Object.keys(emergencyContact).length > 0 && (
                                <Grid item xs={12} md={6}>
                                    <Card 
                                        sx={{ 
                                            borderLeft: `5px solid ${colors.blueAccent[700]}`, 
                                            backgroundColor:theme.palette.mode === "dark" ? colors.primary[500] :colors.grey[900],
                                            height: "100%"
                                        }}
                                    >
                                        <CardContent>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: "bold", 
                                                    mb: 2, 
                                                    color: colors.greenAccent[500] 
                                                }}
                                            >
                                                <WorkIcon sx={{ verticalAlign: "middle", mr: 1 }} /> Emergency Contact Details
                                            </Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Contact Name1:</strong> {emergencyContact?.emergency_contact_name1 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Contact Number1:</strong> {emergencyContact?.emergency_contact_number1 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Person Relation1:</strong> {emergencyContact?.emergency_person_relation1 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Contact Name2:</strong> {emergencyContact?.emergency_contact_name2 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Contact Number2:</strong> {emergencyContact?.emergency_contact_number2 || " "}</Typography>
                                            <Typography color={colors.grey[100]}><strong>Emergency Person Relation2:</strong> {emergencyContact?.emergency_person_relation2 || " "}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default EmployeeDetails;
